import React, { useRef, useEffect, useCallback, useState } from "react";
import { DiffEditor } from "@monaco-editor/react";
import type * as monaco from "monaco-editor";
import { Loader2 } from "lucide-react";
import { cn } from "../lib/utils";
import type { DiffViewMode, MonacoEditorTheme } from "../types";

export interface DiffCodeEditorProps {
  original: string;
  modified: string;
  language?: string;
  fileName?: string;
  theme?: MonacoEditorTheme;
  viewMode?: DiffViewMode;
  readOnly?: boolean;
  editorOptions?: Record<string, unknown>;
  onMount?: (editor: monaco.editor.IStandaloneDiffEditor) => void;
  className?: string;
  /** After accept: cumulative merged file from the **original** (left) model — sync props to this or the next diff resets merges. */
  onHunkAccept?: (mergedBaselineFromOriginal: string) => void;
  /** After reject: `hunkIndex` plus **modified** (right) model text so the parent can sync working copy. */
  onHunkReject?: (hunkIndex: number, updatedModifiedText: string) => void;
  /** Enable per-hunk inline buttons */
  inlineActions?: boolean;
  /**
   * When provided, the component writes a `scrollToHunk(idx)` function into
   * this ref after mount so the parent can drive hunk navigation (← →).
   * Cleared to `null` when the editor unmounts.
   */
  scrollToHunkRef?: React.MutableRefObject<((hunkIndex: number) => void) | null>;
}

/** Represents one diff hunk's visual bar and associated change data. */
type HunkItem = {
  idx: number;
  /** Pixel offset from Monaco editor container top (already scroll-adjusted). */
  top: number;
  change: monaco.editor.ILineChange;
};

/**
 * DiffCodeEditor — Monaco DiffEditor wrapper for inline/side-by-side diff view.
 * Handles model disposal ordering to prevent "TextModel got disposed before DiffEditorWidget" errors.
 *
 * Accept/Reject semantics:
 *   Accept  — copy the modified hunk into the original model (making original = modified for that hunk).
 *   Reject  — restore the original hunk into the modified model (reverting modified = original for that hunk).
 *
 * All edits use getLinesContent() + splice() + pushEditOperations(fullRange) to correctly
 * handle pure-addition, pure-deletion, and mixed hunks without newline edge cases.
 * Monaco ILineChange signals:
 *   originalEndLineNumber === 0  → pure addition (nothing removed from original)
 *   modifiedEndLineNumber === 0  → pure deletion  (nothing added to modified)
 *
 * Per-hunk Accept/Reject buttons are React elements rendered OUTSIDE Monaco's DOM and
 * positioned absolutely over Monaco's passive spacer view zones. This bypasses Monaco's
 * internal pointer-event capture so buttons always receive click events.
 */
export function DiffCodeEditor({
  original,
  modified,
  language = "plaintext",
  theme = "vs-dark",
  viewMode = "inline",
  readOnly = true,
  editorOptions = {},
  onMount,
  className,
  onHunkAccept,
  onHunkReject,
  inlineActions = false,
  scrollToHunkRef,
}: DiffCodeEditorProps) {
  const editorRef = useRef<monaco.editor.IStandaloneDiffEditor | null>(null);
  /** Dispose before editor teardown so in-flight diff computation is not canceled mid-hook (avoids uncaught `Canceled`). */
  const diffUpdateListenerRef = useRef<monaco.IDisposable | null>(null);
  const scrollListenerRef = useRef<monaco.IDisposable | null>(null);
  const layoutListenerRef = useRef<monaco.IDisposable | null>(null);
  /** @monaco-editor/react only invokes `onMount` once; keep latest handlers via ref to avoid stale closures. */
  const onHunkAcceptRef = useRef(onHunkAccept);
  const onHunkRejectRef = useRef(onHunkReject);
  onHunkAcceptRef.current = onHunkAccept;
  onHunkRejectRef.current = onHunkReject;

  /**
   * Ref to the `renderZones` function defined inside handleMount's closure.
   * handleHunkAccept / handleHunkReject call this after each edit to
   * refresh zone spacers and recompute bar positions.
   */
  const renderZonesRef = useRef<() => void>(() => {});

  /**
   * React state: one entry per diff hunk, holding its viewport-relative Y
   * position and the raw Monaco ILineChange data. Drives the Accept/Reject
   * button overlay rendered outside Monaco's DOM.
   */
  const [hunkItems, setHunkItems] = useState<HunkItem[]>([]);

  const mergedOptions: Record<string, unknown> = {
    readOnly,
    renderSideBySide: viewMode === "side-by-side",
    enableSplitViewResizing: true,
    // Expose whitespace-only diffs — the default (true) silently hides them so
    // per-hunk UI never appears for whitespace-only changes. Callers that need
    // the old behaviour can override via editorOptions.
    ignoreTrimWhitespace: false,
    diffWordWrap: "off",
    diffCodeLens: false,
    minimap: { enabled: false },
    fontSize: 14,
    lineNumbers: "on",
    scrollBeyondLastLine: inlineActions,
    automaticLayout: true,
    renderOverviewRuler: true,
    // Inline default shows two line-number columns (old vs new). True inline
    // matches VS Code / Cursor: one gutter + no duplicate margin revert icons
    // when we already render per-hunk Accept/Reject.
    ...(viewMode === "inline"
      ? {
          experimental: { useTrueInlineView: true },
          renderMarginRevertIcon: !inlineActions,
        }
      : {}),
    // Keep suggest/hover-style widgets above clipped parents; helps edge hunks in flex layouts.
    ...(inlineActions ? { fixedOverflowWidgets: true } : {}),
    // XML (metadata) is often one very long line per element; without wrapping a one-token change
    // is a horizontal scroll hunt. ignoreTrimWhitespace stays false above.
    ...(language === "xml" ? { wordWrap: "on", diffWordWrap: "on" } : {}),
    ...editorOptions,
  };

  /**
   * Accept hunk: merge the modified hunk into the original model.
   * Defined at component level (reads editor from ref) so it can be used
   * directly as a React onClick handler — no Monaco DOM involvement.
   */
  const handleHunkAccept = useCallback((item: HunkItem) => {
    const ed = editorRef.current;
    if (!ed) return;
    const modifiedEditor = ed.getModifiedEditor();
    const origEditor = ed.getOriginalEditor();
    const origModel = origEditor.getModel();
    const modModel = modifiedEditor.getModel();
    if (!modModel || !origModel) return;

    const { change } = item;
    const ms = change.modifiedStartLineNumber;
    const me = change.modifiedEndLineNumber;
    const os = change.originalStartLineNumber;
    const oe = change.originalEndLineNumber;
    const isPureAdd = oe === 0;
    const isPureDel = me === 0;
    const origLines = origModel.getLinesContent();
    const modLines = modModel.getLinesContent();

    let newOrigLines: string[];
    if (isPureAdd) {
      const addedLines = ms >= 1 && me >= ms ? modLines.slice(ms - 1, me) : [];
      newOrigLines = [...origLines.slice(0, os), ...addedLines, ...origLines.slice(os)];
    } else if (isPureDel) {
      newOrigLines = [...origLines.slice(0, os - 1), ...origLines.slice(oe)];
    } else {
      const replacementLines = ms >= 1 && me >= ms ? modLines.slice(ms - 1, me) : [];
      newOrigLines = [...origLines.slice(0, os - 1), ...replacementLines, ...origLines.slice(oe)];
    }

    try {
      origEditor.updateOptions({ readOnly: false });
      origModel.pushEditOperations(
        [],
        [{ range: origModel.getFullModelRange(), text: newOrigLines.join("\n") }],
        () => null,
      );
      origEditor.updateOptions({ readOnly: true });
      onHunkAcceptRef.current?.(origModel.getValue());
      queueMicrotask(() => { try { renderZonesRef.current(); } catch { /* disposed */ } });
    } catch { /* line mapping shifted after a prior hunk — skip silently */ }
  }, []);

  /**
   * Reject hunk: restore the original content in the modified model.
   */
  const handleHunkReject = useCallback((item: HunkItem) => {
    const ed = editorRef.current;
    if (!ed) return;
    const modifiedEditor = ed.getModifiedEditor();
    const origEditor = ed.getOriginalEditor();
    const origModel = origEditor.getModel();
    const modModel = modifiedEditor.getModel();
    if (!modModel || !origModel) return;

    const { idx, change } = item;
    const ms = change.modifiedStartLineNumber;
    const me = change.modifiedEndLineNumber;
    const os = change.originalStartLineNumber;
    const oe = change.originalEndLineNumber;
    const isPureAdd = oe === 0;
    const isPureDel = me === 0;
    const origLines = origModel.getLinesContent();
    const modLines = modModel.getLinesContent();

    let newModLines: string[];
    if (isPureAdd) {
      newModLines = [...modLines.slice(0, ms - 1), ...modLines.slice(me)];
    } else if (isPureDel) {
      const restoredLines = origLines.slice(os - 1, oe);
      newModLines = [...modLines.slice(0, ms), ...restoredLines, ...modLines.slice(ms)];
    } else {
      const restoredLines = origLines.slice(os - 1, oe);
      newModLines = [...modLines.slice(0, ms - 1), ...restoredLines, ...modLines.slice(me)];
    }

    try {
      modifiedEditor.updateOptions({ readOnly: false });
      modModel.pushEditOperations(
        [],
        [{ range: modModel.getFullModelRange(), text: newModLines.join("\n") }],
        () => null,
      );
      modifiedEditor.updateOptions({ readOnly: true });
      onHunkRejectRef.current?.(idx, modModel.getValue());
      queueMicrotask(() => { try { renderZonesRef.current(); } catch { /* disposed */ } });
    } catch { /* line numbers shifted — skip silently */ }
  }, []);

  const handleMount = useCallback((editor: monaco.editor.IStandaloneDiffEditor) => {
    editorRef.current = editor;
    onMount?.(editor);

    // Inline diff: ensure true-inline mode and a single line-number column.
    // `experimental` in create options can race Monaco layout; re-apply after mount.
    if (viewMode === "inline") {
      const tuneInlineGutter = () => {
        try {
          editor.updateOptions({
            experimental: { useTrueInlineView: true },
            renderMarginRevertIcon: !inlineActions,
          } as Record<string, unknown>);
          editor.getOriginalEditor().updateOptions({
            lineNumbers: "off",
            glyphMargin: false,
            folding: false,
            lineDecorationsWidth: 0,
            lineNumbersMinChars: 0,
          });
          editor.getModifiedEditor().updateOptions({
            lineNumbers: "on",
            glyphMargin: false,
          });
        } catch {
          /* older monaco */
        }
      };
      tuneInlineGutter();
      requestAnimationFrame(tuneInlineGutter);
      window.setTimeout(tuneInlineGutter, 120);
    }

    if (inlineActions) {
      // Clean up any listeners from a prior mount (e.g. editor key change).
      diffUpdateListenerRef.current?.dispose();
      diffUpdateListenerRef.current = null;
      scrollListenerRef.current?.dispose();
      scrollListenerRef.current = null;
      layoutListenerRef.current?.dispose();
      layoutListenerRef.current = null;

      const addedZoneIds: string[] = [];
      const modifiedEditor = editor.getModifiedEditor();

      /**
       * Compute viewport-relative Y positions for each hunk's spacer zone and
       * push to React state. Called after zone render and on every scroll/layout
       * change to keep React button overlay in sync with Monaco's scroll position.
       */
      const syncBarPositions = (
        changes: monaco.editor.ILineChange[],
        modifiedLineCount: number,
      ) => {
        const scrollTop = modifiedEditor.getScrollTop();
        const items: HunkItem[] = changes.map((change, idx) => {
          const isPureDeletion = change.modifiedEndLineNumber === 0;
          let afterLine = isPureDeletion
            ? change.modifiedStartLineNumber
            : change.modifiedEndLineNumber;
          if (afterLine < 0) afterLine = 0;
          if (modifiedLineCount > 0 && afterLine > modifiedLineCount) {
            afterLine = modifiedLineCount;
          }
          // getTopForLineNumber(afterLine + 1) = pixel top of the line after the hunk,
          // which is exactly where Monaco places the spacer view zone.
          const top =
            modifiedEditor.getTopForLineNumber(Math.max(1, afterLine + 1)) - scrollTop;
          return { idx, top, change };
        });
        setHunkItems(items);
      };

      const renderZones = () => {
        const changes = editor.getLineChanges();

        // Always remove prior zones first — when the last hunk is merged,
        // `changes` becomes empty; we must still clear or the spacer sticks.
        if (addedZoneIds.length > 0) {
          modifiedEditor.changeViewZones((accessor) => {
            for (const id of addedZoneIds) {
              try { accessor.removeZone(id); } catch { /* already gone */ }
            }
          });
          addedZoneIds.length = 0;
        }

        if (!changes?.length) {
          setHunkItems([]);
          return;
        }

        const modModel = modifiedEditor.getModel();
        const modifiedLineCount = modModel?.getLineCount() ?? 0;

        changes.forEach((change, idx) => {
          const isPureDeletion = change.modifiedEndLineNumber === 0;
          let afterLine = isPureDeletion
            ? change.modifiedStartLineNumber
            : change.modifiedEndLineNumber;
          if (afterLine < 0) afterLine = 0;
          if (modifiedLineCount > 0 && afterLine > modifiedLineCount) {
            afterLine = modifiedLineCount;
          }

          // Passive spacer — 28 px gap in Monaco's layout between hunks.
          // pointer-events:none: zone never intercepts clicks.
          // The Accept/Reject buttons are React elements OUTSIDE Monaco's DOM.
          const domNode = document.createElement("div");
          domNode.style.cssText =
            "height:28px;box-sizing:border-box;pointer-events:none;" +
            "border-top:1px solid rgba(128,128,128,0.12);";

          modifiedEditor.changeViewZones((accessor) => {
            const zoneId = accessor.addZone({
              afterLineNumber: afterLine,
              heightInPx: 28,
              domNode,
              suppressMouseDown: false,
              ordinal: idx * 10,
            });
            addedZoneIds.push(zoneId);
          });
        });

        syncBarPositions(changes, modModel?.getLineCount() ?? 0);
      };

      // Expose renderZones directly for handleHunkAccept / handleHunkReject
      // (defined at component level). Each caller wraps in queueMicrotask.
      renderZonesRef.current = renderZones;

      // Expose imperative scroll-to-hunk so the parent can drive ← → navigation.
      if (scrollToHunkRef) {
        scrollToHunkRef.current = (hunkIndex: number) => {
          const changes = editor.getLineChanges();
          const change = changes?.[hunkIndex];
          if (!change) return;
          const line =
            change.modifiedEndLineNumber > 0
              ? change.modifiedEndLineNumber
              : change.modifiedStartLineNumber;
          if (line > 0) {
            editor.getModifiedEditor().revealLineInCenterIfOutsideViewport(line);
          }
        };
      }

      // Re-position React buttons whenever Monaco scrolls or its layout changes.
      scrollListenerRef.current = modifiedEditor.onDidScrollChange(() => {
        const changes = editor.getLineChanges();
        if (!changes?.length) { setHunkItems([]); return; }
        syncBarPositions(changes, modifiedEditor.getModel()?.getLineCount() ?? 0);
      });
      layoutListenerRef.current = modifiedEditor.onDidLayoutChange(() => {
        const changes = editor.getLineChanges();
        if (!changes?.length) return;
        syncBarPositions(changes, modifiedEditor.getModel()?.getLineCount() ?? 0);
      });

      // Listen for diff computation completion. This fires whenever Monaco finishes
      // recomputing the diff (initial load + any updates). We also call once
      // immediately in case the diff is already ready when we mount.
      diffUpdateListenerRef.current = editor.onDidUpdateDiff(renderZones);
      renderZones();
    }
  }, [onMount, viewMode, inlineActions]);

  // Store monaco instance from the onMount callback so cleanup can use it
  // Clean up: reset the diff editor models BEFORE React unmounts the DOM,
  // preventing "TextModel got disposed before DiffEditorWidget model got reset"
  useEffect(() => {
    // Nothing to set up: this effect exists ONLY for its teardown (see below). The monaco
    // instance it used to fetch eagerly was needed to build the placeholder models that the
    // detach no longer creates.
    return () => {
      try {
        try { diffUpdateListenerRef.current?.dispose(); } catch { /* listener already disposed */ }
        diffUpdateListenerRef.current = null;
        try { scrollListenerRef.current?.dispose(); } catch { /* */ }
        scrollListenerRef.current = null;
        try { layoutListenerRef.current?.dispose(); } catch { /* */ }
        layoutListenerRef.current = null;
        if (scrollToHunkRef) scrollToHunkRef.current = null;

        const editor = editorRef.current;
        if (editor) {
          try {
            /*
             * `setModel(null)` — DETACH, do not swap in placeholders.
             *
             * This used to create two empty models, hand them to `setModel`, and dispose them on
             * the very next line. `setModel` on a diff widget starts an ASYNCHRONOUS diff
             * computation against the models it was just given, so disposing them synchronously
             * destroyed them mid-computation and produced exactly the error this cleanup exists to
             * prevent: "TextModel got disposed before DiffEditorWidget model got reset". It was
             * invisible for as long as no caller rendered a real diff and then unmounted it — the
             * Weave QA harness answered the per-file diff route with the wrong shape, so Monaco
             * never mounted there, and the other consumers unmount with the page rather than
             * mid-session. Serving a real diff in the harness surfaced it on the first run.
             *
             * Monaco's `IDiffEditor.setModel` accepts `null`, which detaches both sides without
             * creating anything to dispose. Nothing is left mid-computation and the widget resets
             * before React takes the DOM away.
             */
            editor.setModel(null);
          } catch {
            // Swallow — editor may already be partially disposed; Monaco may throw `Canceled`
          }
        }
        editorRef.current = null;
      } catch {
        /* Monaco sometimes surfaces `Canceled` from async diff teardown */
      }
    };
  }, []);

  return (
    <div className={cn("h-full flex flex-col bg-background", className)}>
      {/*
        position:relative makes this the containing block for the absolutely-
        positioned React Accept/Reject button overlay.
        pt-1: small top gap so the first spacer zone is not flush with the edge.
        pb-14: extra scroll room so the last hunk's spacer (and its overlay button)
        can be scrolled fully into view without scrollbar overlap.
      */}
      <div
        className={cn(
          "relative flex-1 min-h-0",
          inlineActions && "pt-1 pb-14",
        )}
      >
        <DiffEditor
          height="100%"
          language={language}
          original={original}
          modified={modified}
          theme={theme}
          onMount={handleMount}
          options={mergedOptions}
          loading={
            <div className="flex items-center justify-center h-full text-muted-foreground">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="h-8 w-8 animate-spin" />
                <p className="text-sm">Loading diff...</p>
              </div>
            </div>
          }
        />

        {/*
          Per-hunk Accept/Reject actions — React elements outside Monaco's DOM so
          clicks are never intercepted by Monaco's capture-phase handlers.

          Coordinate maths:
            item.top  = getTopForLineNumber(afterLine+1) - scrollTop
                      = Monaco viewport Y of the line immediately AFTER the hunk.
            The 28 px spacer zone lives at [item.top-28, item.top] in Monaco
            viewport space.  The wrapper has pt-1 (4 px), so in div coords:
              spacer top  = item.top - 28 + 4 = item.top - 24
              spacer bot  = item.top      + 4 = item.top + 4  ← was the old top!
            We pin buttons to the spacer top so they fill the empty visual band
            and never sit on top of code text.
        */}
        {inlineActions && hunkItems.map((item) => (
          <div
            key={item.idx}
            style={{
              position: "absolute",
              top: item.top - 24,
              right: 12,
              height: 28,
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              gap: 4,
              pointerEvents: "auto",
            }}
          >
            <button
              type="button"
              onClick={() => handleHunkReject(item)}
              title="Reject this change (restore original)"
              style={{
                fontSize: 11,
                padding: "1px 8px",
                borderRadius: 4,
                border: "1px solid rgba(239,68,68,0.5)",
                background: "rgba(239,68,68,0.08)",
                color: "#ef4444",
                cursor: "pointer",
                fontWeight: 600,
                lineHeight: "20px",
                whiteSpace: "nowrap",
              }}
            >
              ✕ Reject
            </button>
            <button
              type="button"
              onClick={() => handleHunkAccept(item)}
              title="Accept this change (apply to file)"
              style={{
                fontSize: 11,
                padding: "1px 8px",
                borderRadius: 4,
                border: "none",
                background: "#16a34a",
                color: "#fff",
                cursor: "pointer",
                fontWeight: 600,
                lineHeight: "20px",
                whiteSpace: "nowrap",
              }}
            >
              ✓ Accept
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
